<script setup lang="ts">
  import * as z from 'zod';
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import type { FixedTextProperties as FixedTextPropsType } from '../../../model/form.types';
  import { FORM_FIELD_CHARACTER_LIMITS } from '../../../lib/form-field-character-limits';
  import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Input,
  } from '@/shared/ui';

  interface Props {
    elementId?: string;
  }

  const props = defineProps<Props>();

  function toSingleLine(value?: string): string {
    return (value ?? '').replace(/[\r\n]+/g, ' ').trim();
  }

  type FormValues = {
    name: string;
    content?: string;
  };

  usePropertyForm<FormValues, FixedTextPropsType>({
    elementId: () => props.elementId,
    validationSchema: z.object({
      name: z.string().min(1, 'O nome é obrigatório').max(FORM_FIELD_CHARACTER_LIMITS.FIELD_ID),
      content: z
        .string()
        .min(1, 'Texto Fixo não pode ser vazio.')
        .max(FORM_FIELD_CHARACTER_LIMITS.FIXED_TEXT),
    }),
    extractInitialValues: (properties: FixedTextPropsType | undefined): FormValues => ({
      name: properties?.name ?? '',
      content: toSingleLine(properties?.content),
    }),
    mapValuesToProperties: (values: FormValues, currentProperties: FixedTextPropsType) => ({
      properties: {
        ...currentProperties,
        name: values.name || currentProperties.name,
        content:
          values.content !== undefined ? toSingleLine(values.content) : currentProperties.content,
      },
    }),
  });
</script>

<template>
  <div class="flex flex-col">
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="name"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            ID do campo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.FIELD_ID"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="content"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Texto fixo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui o texto fixo"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.FIXED_TEXT"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>
  </div>
</template>
